@switch (state()) {
  @case ('loading') {
    <div class="page-center">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
  }

  @case ('error') {
    <div class="page-center">
      <div class="hub-card ta--center">
        <p class="body-300 color-gray-400 mb-6">{{ contextError()?.message }}</p>

        @if (contextError()?.retriable) {
          <button andButton color="primary" size="large" (click)="retry()">
            Try again
          </button>
        }
      </div>
    </div>
  }

  @case ('viewer-only') {
    <div class="signing-page">
      <div class="viewer-only-banner body-400 color-gray-100 ta--center">
        This document is for your review only.
      </div>

      <div class="viewer-area">
        @let context = signingContext();
        @if (context) {
          <app-esign-signing-viewer
            [originalFileUrl]="context.file.originalFileUrl"
            [combinedXfdf]="context.file.combinedXfdf"
            [recipientId]="context.recipient.id"
            [filename]="context.bundle.name + '.pdf'"
            mode="view-only"
            (loadError)="onViewerLoadError()"
          ></app-esign-signing-viewer>
        }
      </div>
    </div>
  }

  @case ('completed') {
    @if (viewingDocument()) {
      <!-- "View Document" from the completion screen: the same read-only viewer the VIEWER
           recipient type gets, with a way back to the confirmation. -->
      <div class="signing-page">
        <div class="viewer-only-banner d-flex align-items-center justify-content-between">
          <button andButton color="ghost" (click)="backToCompletion()" dataSelector="esign-view-document-back">
            <aiwyn-icon name="arrow-left" height="16" width="16" class="me-2"></aiwyn-icon> Back
          </button>
        </div>

        <div class="viewer-area">
          @let context = signingContext();
          @if (context) {
            <app-esign-signing-viewer
              [originalFileUrl]="context.file.originalFileUrl"
              [combinedXfdf]="context.file.combinedXfdf"
              [recipientId]="context.recipient.id"
              [filename]="context.bundle.name + '.pdf'"
              mode="view-only"
              (loadError)="onViewerLoadError()"
            ></app-esign-signing-viewer>
          }
        </div>
      </div>
    } @else {
      <div class="page-center">
        <div class="hub-card completion-card" dataSelector="esign-completion-card">
          @if (justSigned()) {
            <!-- Figma 4.0 / 4.1 — Confirmation: the signer submitted in this session. -->
            <h2 class="h2-500 color-gray-100 completion-title" dataSelector="esign-completion-title">Document signed</h2>
            <p class="body-300 color-gray-400 completion-subtitle">
              Thank you, {{ firstName() }}. A signed copy will also be sent to your email and available in your portal
              account{{ allSigned() ? ' if you have created one.' : ' once all recipients have signed.' }}
            </p>
          } @else {
            <!-- Figma 5.0 / 5.1 — Completion: the signer is returning to a link they already signed. -->
            <h2 class="h2-500 color-gray-100 completion-title" dataSelector="esign-completion-title">You've already signed</h2>
            <p class="body-300 color-gray-400 completion-subtitle">
              @if (signedAt(); as signedOn) {
                You signed on {{ signedOn | safeDate: 'MMMM d, y' }}.
              }
              {{
                allSigned()
                  ? 'Your signed copy is available below and a copy emailed to all recipients.'
                  : 'A signed copy will be sent to your email once all recipients have signed.'
              }}
            </p>
          }

          <app-esign-signer-roster [signers]="signers()"></app-esign-signer-roster>

          <div class="d-flex align-items-center justify-content-end gap-3 completion-actions">
            @if (!justSigned()) {
              <button andButton color="secondary" size="large" (click)="viewDocument()" dataSelector="esign-view-document">
                View Document
              </button>
            }

            @if (canDownload()) {
              <button andButton color="secondary" size="large" (click)="downloadSignedCopy()" dataSelector="esign-download-signed-copy">
                <aiwyn-icon name="download" height="16" width="16" class="me-2"></aiwyn-icon> Download Signed Copy
              </button>
            }

            @if (justSigned()) {
              <button andButton color="primary" size="large" (click)="returnToPortal()" dataSelector="esign-return-to-portal">
                Return to Portal <aiwyn-icon name="arrow-right" height="16" width="16" class="ms-2"></aiwyn-icon>
              </button>
            }
          </div>
        </div>

        @if (!justSigned()) {
          <!-- 5.x puts Return to Portal outside the card as a plain link, not a button. -->
          <button class="body-300 return-link" (click)="returnToPortal()" dataSelector="esign-return-to-portal">Return to Portal</button>
        }
      </div>
    }
  }
}

@if (state() === 'hub') {
  <div class="page-center">
    @let context = signingContext();
    @if (context) {
      <div class="hub-card">
        <h2 class="h2-500 color-gray-100 mb-6">Review and sign {{ context.bundle.name }}</h2>

        <app-esign-signer-roster [signers]="signers()" class="mb-6"></app-esign-signer-roster>

        <p class="small-300 color-gray-400 mb-4">
          By proceeding, you confirm your electronic signature to be as valid as if you signed the document by
          hand, to the full extent allowed by applicable law.
        </p>

        @if (consentError()) {
          <div class="alert alert--error mb-4">{{ consentError() }}</div>
        }

        <button
          andButton
          color="primary"
          size="large"
          class="w-100"
          [isLoading]="acceptingConsent()"
          [disabled]="acceptingConsent()"
          (click)="getStarted()"
        >
          Get Started <aiwyn-icon name="arrow-right" height="16" width="16" class="ms-2"></aiwyn-icon>
        </button>
      </div>
    }
  </div>
}

@if (enteredSigning()) {
  <!-- Stays mounted (shown/hidden via [hidden], not @switch) across Back <-> Get Started so the
       live WebViewer session — and whatever the signer has already filled in — survives the toggle. -->
  <div class="signing-page" [hidden]="state() !== 'signing'">
    <div class="viewer-area">
      @let context = signingContext();
      @if (context) {
        <app-esign-signing-viewer
          [originalFileUrl]="context.file.originalFileUrl"
          [combinedXfdf]="context.file.combinedXfdf"
          [recipientId]="context.recipient.id"
          [filename]="context.bundle.name + '.pdf'"
          [reloadToken]="viewerReloadToken()"
          mode="sign"
          (fieldsChanged)="onFieldsChanged($event)"
          (submitReady)="onSubmitReady($event)"
          (loadError)="onViewerLoadError()"
          (exportError)="onExportError()"
        ></app-esign-signing-viewer>
      }
    </div>

    @if (submitError()) {
      <div class="alert alert--error submit-error">{{ submitError() }}</div>
    }

    <div class="footer-nav d-flex align-items-center justify-content-between px-6">
      <button andButton color="ghost" (click)="backToHub()">
        <aiwyn-icon name="arrow-left" height="16" width="16" class="me-2"></aiwyn-icon> Back
      </button>

      <div class="d-flex align-items-center gap-6">
        <span class="body-300 color-gray-400">
          {{ fieldsProgress().completed }} of {{ fieldsProgress().total }} fields complete
        </span>

        <button
          andButton
          color="primary"
          size="large"
          [disabled]="!isReadyToSubmit()"
          [isLoading]="submitting()"
          (click)="onSubmitClick()"
        >
          {{ isReadyToSubmit() ? 'Submit' : 'Create Signature' }}
          <aiwyn-icon name="arrow-right" height="16" width="16" class="ms-2"></aiwyn-icon>
        </button>
      </div>
    </div>
  </div>
}
